import type { ReactElement } from "react";
import { Navigate, type RouteObject } from "react-router";
import { TrainingTeammatePageKnowledgeBaseAdherenceStatement } from "../../features/teammatePerformance/trainingTeammatePageKnowledgeBaseAdherenceStatement.js";
import { TrainingTeammatePageOverviewGrading } from "../../features/teammatePerformance/trainingTeammatePageOverviewGrading.js";
import { TrainingTeammatePageOverviewGradingAssessment } from "../../features/teammatePerformance/trainingTeammatePageOverviewGradingAssessment.js";
import { TrainingTeammatePageOverviewGradingTranscript } from "../../features/teammatePerformance/trainingTeammatePageOverviewGradingTranscript.js";
import { TrainingTeammatePagePlaybookCriterion } from "../../features/teammatePerformance/trainingTeammatePagePlaybookCriterion.js";
import { TrainingTeammatePageRecordingGrade } from "../../features/teammatePerformance/trainingTeammatePageRecordingGrade.js";
import { defineHandle } from "../navigation/appRouteHandle.js";
import type { RouteParams } from "../routeParams.js";
import { GradingTupleRedirect } from "./compatRedirects.js";

/**
 * Builds the recording grade page's URL for a different criterion of the
 * same grading: everything but `criterionId` comes from the current URL's
 * params (via `requireRouteParam`), fed to the family's `appPaths.x.build`.
 */
export type BuildCriterionPath = (
  params: RouteParams,
  criterionId: string,
) => string;

export function createRecordingGradeElement(
  buildCriterionPath: BuildCriterionPath,
): ReactElement {
  return (
    <TrainingTeammatePageRecordingGrade
      buildCriterionPath={buildCriterionPath}
    />
  );
}

/**
 * Grounds the research agent to the call in a route's `:gradingId`. The call
 * in front of the user outranks whatever page it opened over, so this fragment
 * overrides the ancestors' `page`. Shared by the route factories rather than
 * declared at each mount: every route tree opens the same call views.
 */
const customerInteractionHandle = defineHandle({
  pageContext: ({ params }) => ({
    page: "customerInteraction",
    gradingId: params.gradingId,
  }),
});

export function createGradingSlideoverRoute({
  path,
  buildCriterionPath,
}: {
  path: string;
  buildCriterionPath: BuildCriterionPath;
}): RouteObject {
  return {
    path,
    // Grading detail is keyed purely by `:gradingId`. The page renders nested
    // views via its own `<Outlet />`.
    Component: TrainingTeammatePageOverviewGrading,
    handle: customerInteractionHandle,
    children: [
      {
        index: true,
        element: <Navigate to="assessment" replace />,
      },
      {
        path: "assessment",
        Component: TrainingTeammatePageOverviewGradingAssessment,
        children: [
          {
            path: "criteria/:criterionId",
            element: createRecordingGradeElement(buildCriterionPath),
          },
          {
            path: "knowledge-base-adherence/:statementId",
            Component: TrainingTeammatePageKnowledgeBaseAdherenceStatement,
          },
        ],
      },
      {
        path: "transcript",
        Component: TrainingTeammatePageOverviewGradingTranscript,
      },
    ],
  };
}

/**
 * The criterion drill-down subtree: the criterion detail page with its
 * `customer-interactions/:gradingId` recording grade child and the
 * backwards-compatibility `accounts/:accountId` tuple redirect.
 */
export function createCriterionDrilldownRoute({
  path,
  buildCriterionPath,
  buildTupleRedirectTo,
  appliesHasResultDefault = true,
}: {
  path: string;
  buildCriterionPath: BuildCriterionPath;
  /** Builds the tuple redirect's destination grading page URL. */
  buildTupleRedirectTo: (params: RouteParams, gradingId: string) => string;
  /**
   * Whether the pages this drilldown opens over default to excluding calls
   * without grades ("Include calls without grades" in the Playbook results
   * menu). The dialog reads the same criteria-scores query as the page behind
   * it, so a mismatch would show two different call counts for one page. True
   * everywhere except the agents workspace, which has never applied it.
   */
  appliesHasResultDefault?: boolean;
}): RouteObject {
  return {
    path,
    element: (
      <TrainingTeammatePagePlaybookCriterion
        appliesHasResultDefault={appliesHasResultDefault}
      />
    ),
    children: [
      {
        path: "customer-interactions/:gradingId",
        element: createRecordingGradeElement(buildCriterionPath),
        handle: customerInteractionHandle,
      },
      {
        path: "accounts/:accountId",
        element: <GradingTupleRedirect buildTo={buildTupleRedirectTo} />,
      },
    ],
  };
}
